
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Camera, CameraModel } from '../../../../core/models/organization.interface';
import { combineLatest, startWith, Subject, takeUntil } from 'rxjs';
import { Building, Floor, Room } from '../../../../core/models/organization.interface';
import { GlobalSettings } from '../../../../core/models/global-settings.model';
import { WindowService } from '../../../../core/services/window.service';
import { OrganizationService } from '../../../../core/services/organization.service';
import { FmvAttributeVisualizerModalComponent } from './fmv-attribute-visualizer-modal/fmv-attribute-visualizer-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CamerainfouiComponent } from './camerainfoui/camerainfoui.component';
import { OrganizationUtilService } from '../../../../core/services/organization-util.service';
import { CameraService } from '../../../../core/services/camera.service';
import * as Cesium from 'cesium';
import { FmvAttributeVisualizerService } from '../../../../core/services/fmv-attribute-visualizer.service';
interface SensorListResponse {
  buildings: Building[];
}

@Component({
  selector: 'app-camera-list',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './camera-list.component.html',
  styleUrl: './camera-list.component.css'
})
export class CameraListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @Input() globalSettings?: GlobalSettings;
  @Input() cameras: CameraModel[] = [];

  private destroy$ = new Subject<void>();

  selectedOption: 'building' | 'floor' | 'room' = 'building';
  searchQuery: string = '';
  scrollHeight: number;

  buildingList: Building[] = [];
  floorList: Floor[] = [];
  roomList: Room[] = [];
  filteredCameras: CameraModel[] = [];

  selectedBuilding?: Building;
  selectedFloor?: Floor;
  selectedRoom?: Room;

  filterForm: FormGroup;
  sensorData?: SensorListResponse;

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService,
    private windowService: WindowService,
    private modalService: NgbModal,
    private organizationutil: OrganizationUtilService,
    private cameraService: CameraService,
    private fmvAttributeVisualizerService: FmvAttributeVisualizerService
  ) {
    this.scrollHeight = this.windowService.getInnerHeight() * 0.75;
    this.filterForm = this.fb.group({
      building: [null],
      floor: [null],
      room: [null]
    });
  }

  ngOnInit(): void {
    if (this.cameras && this.cameras.length > 0) {
      this.filteredCameras = [...this.cameras];
      this.processCameraData();
      this.assignInitValues();
    } else {
      this.organizationService.getSensorList()
        .pipe(takeUntil(this.destroy$))
        .subscribe(sensorData => {
          this.cameras = sensorData.cameraList;
          this.filteredCameras = [...this.cameras];
          this.processCameraData();
          this.assignInitValues();
        });
    }
    this.setupFormSubscriptions();
  }

 private assignInitValues(): void {
    this.buildingList = this.getBuildingList();
    this.selectedBuilding = this.buildingList[0];
    this.floorList = this.getFloorList(this.selectedBuilding);
    this.selectedFloor = this.floorList[0];
    this.roomList = this.getRoomList(this.selectedFloor);
    this.selectedRoom = this.roomList[0];
    this.updateFilteredList();
  }

  private processCameraData(): void {
    const buildingMap: { [id: string]: Building } = {};
    const floorMap: { [id: string]: Floor & { rooms: Room[] } } = {};

    for (const camera of this.cameras) {
      if (camera.building && camera.building.id) {
        let building = buildingMap[camera.building.id];
        if (!building) {
          building = {
            id: camera.building.id,
            label: camera.building.label,
            labelShort: camera.building.labelShort || '',
            numberCamera: 0,
            floors: [],
            attachedCameras: [],
            address: '',
          };
          buildingMap[camera.building.id] = building;
        }
        building.numberCamera++;
        building.attachedCameras.push(camera);

        if (camera.floor && camera.floor.id) {
          let floor = floorMap[camera.floor.id];
          if (!floor) {
            floor = {
              id: camera.floor.id,
              label: camera.floor.label,
              number: camera.floor.number ?? '',
              numberCamera: 0,
              buildingId: building.id,
              rooms: [],
            };
            floorMap[camera.floor.id] = floor;
            building.floors.push(floor);
          }
         floor.numberCamera = (floor.numberCamera ?? 0) + 1;

          if (camera.room && camera.room.id) {
            let room = floor.rooms.find(r => r.id === camera.room!.id);
            if (!room) {
              floor.rooms.push({
                id: camera.room.id,
                label: camera.room.label,
                labelShort: camera.room.labelShort || '',
                numberCamera: 1,
                floorId: floor.id,
                buildingId: building.id,
              });
            } else {
              room.numberCamera = (room?.numberCamera ?? 0) + 1;
            }
          }
        }
      }
    }

    this.sensorData = {
      buildings: Object.values(buildingMap)
    };

    this.buildingList = [
      {
        id: '0',
        label: 'all buildings',
        floors: [],
        numberCamera: this.cameras.length,
        attachedCameras: [],
        address: '',
        labelShort: ''
      },
      ...this.sensorData.buildings
    ];

    this.floorList = this.getFloorList(this.buildingList[0]);
    this.roomList = this.getRoomList(this.floorList[0]);
  }
  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    this.windowService.onResize()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.scrollHeight = this.windowService.getInnerHeight() * 0.75;
      });
  }

  selectOption(option: 'building' | 'floor' | 'room'): void {
    this.selectedOption = option;

    if (option === 'building') {
      this.selectedFloor = undefined;
      this.selectedRoom = undefined;
    } else if (option === 'floor') {
      this.selectedRoom = undefined;
    }

    this.updateFilteredList();
  }

  buildingChanged(): void {
    this.floorList = this.getFloorList(this.selectedBuilding);
    this.selectedFloor = this.floorList[0];
    this.roomList = this.getRoomList(this.selectedFloor);
    this.selectedRoom = this.roomList[0];
    this.updateFilteredList();
  }

  floorChanged(): void {
    this.roomList = this.getRoomList(this.selectedFloor);
    this.selectedRoom = this.roomList[0];
    this.updateFilteredList();
  }

  roomChanged(): void {
    this.updateFilteredList();
  }

  filterCameras(): void {
    this.updateFilteredList();
  }

  private updateFilteredList(): void {
    let filtered = this.cameras;

    if (this.selectedBuilding && this.selectedBuilding.id !== '0') {
      filtered = filtered.filter(camera =>
        camera.building?.id === this.selectedBuilding?.id
      );
    }

    if (this.selectedFloor && this.selectedFloor.id !== '0') {
      filtered = filtered.filter(camera =>
        camera.floor?.id === this.selectedFloor?.id
      );
    }

    if (this.selectedRoom && this.selectedRoom.id !== '0') {
      filtered = filtered.filter(camera =>
        camera.room?.id === this.selectedRoom?.id
      );
    }

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      const query = this.searchQuery.trim().toLowerCase();
      filtered = filtered.filter(camera =>
        (camera.label && camera.label.toLowerCase().includes(query)) ||
        (camera.labelShort && camera.labelShort.toLowerCase().includes(query)) 
      );
    }

    this.filteredCameras = filtered;
  }

  private setupFormSubscriptions(): void {
    this.filterForm.get('building')?.valueChanges
      .pipe(startWith(null), takeUntil(this.destroy$))
      .subscribe(building => {
        this.selectedBuilding = building;
        this.buildingChanged();
        this.floorList = this.getFloorList(building);
        this.filterForm.patchValue({ floor: null, room: null });
        this.roomList = [];
      });

    this.filterForm.get('floor')?.valueChanges
      .pipe(startWith(null), takeUntil(this.destroy$))
      .subscribe(floor => {
        this.selectedFloor = floor;
        this.floorChanged();
        this.roomList = this.getRoomList(floor);
        this.filterForm.patchValue({ room: null });
      });

    this.filterForm.get('room')?.valueChanges
      .pipe(startWith(null), takeUntil(this.destroy$))
      .subscribe(room => {
        this.selectedRoom = room;
        this.roomChanged();
      });

    // Watch all form changes for final selection
    combineLatest([
      this.filterForm.get('building')?.valueChanges.pipe(startWith(null)) || [],
      this.filterForm.get('floor')?.valueChanges.pipe(startWith(null)) || [],
      this.filterForm.get('room')?.valueChanges.pipe(startWith(null)) || []
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([building, floor, room]) => {
        // Handle final selection logic here
      });
  }

  private updateBuildingList(): void {
    this.organizationService.getSensorList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: SensorListResponse) => {

        this.sensorData = result;
        this.buildingList = this.getBuildingList();
      });
  }

  private getBuildingList(): Building[] {
    const buildings = this.sensorData?.buildings || [];
    const total = buildings.reduce((sum, building) => sum + (building.numberCamera ?? 0), 0);
    return [
      {
        id: '0',
        numberCamera: total,
        label: 'all buildings',
        floors: [],
        attachedCameras: [],
        address: '',
        labelShort: ''
      },
      ...buildings
    ];
  }

  private getFloorList(building?: Building): Floor[] {
    if (!building || building.id === '0') {
      const allFloors: Floor[] = [];
      (this.sensorData?.buildings || []).forEach(b => {
        (b.floors || []).forEach(floor => allFloors.push(floor));
      });
      const total = allFloors.reduce((sum, floor) => sum + (floor.numberCamera ?? 0), 0);
      return [{
        id: '0',
        label: 'all floors',
        numberCamera: total,
        rooms: [],
        number: undefined,
        buildingId: ''
      }, ...allFloors];
    }
    const total = building.numberCamera ?? 0;
    return [{
      id: '0',
      label: 'all floors',
      numberCamera: total,
      rooms: [],
      number: undefined,
      buildingId: building.id
    }, ...(building.floors ?? [])];
  }

  private getRoomList(floor?: Floor): Room[] {
    if (!floor || floor.id === '0') {
      const allRooms: Room[] = [];
      (this.floorList || []).forEach(f => {
        (f.rooms || []).forEach(room => allRooms.push(room));
      });
      const total = allRooms.reduce((sum, room) => sum + (room.numberCamera ?? 0), 0);
      return [{
        id: '0',
        label: 'all rooms',
        numberCamera: total,
        labelShort: '',
        floorId: '',
        buildingId: ''
      }, ...allRooms];
    }
    const total = floor.numberCamera ?? 0;
    return [{
      id: '0',
      label: 'all rooms',
      numberCamera: total,
      labelShort: '',
      floorId: floor.id,
      buildingId: floor.buildingId
    }, ...(floor.rooms ?? [])];
  }

  showAdHocVideo(): void {
    const number = this.fmvAttributeVisualizerService.getAdHocCameraSessionNumber() + 1;
    const modalRef = this.modalService.open(FmvAttributeVisualizerModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.type = 'adhoc';
    modalRef.componentInstance.number = number;
    this.fmvAttributeVisualizerService.incAdHocCameraSessionNumber();
  }


  viewPerimeter(camera: CameraModel): void {
    if (!camera.perimeterId) {
      return;
    }
    const area = this.organizationutil.data.perimeterAreaMap[camera.perimeterId];
    if (area && area.building && area.building.pin) {
      const pin = area.building.pin;
      this.cameraService.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          pin.object.geometry.coordinates[0],
          pin.object.geometry.coordinates[1],
          500
        ),
        complete: () => {
          pin.viewDetailsWindow(pin);
        }
      });
    }
  }

  viewInfo(camera: CameraModel): void {
    const sensorId = camera.id;
    const cameraId = Array.isArray((camera as any).camera) && (camera as any).camera.length > 0
      ? (camera as any).camera[0].id
      : undefined;

    if (sensorId && cameraId) {
      const modalRef = this.modalService.open(CamerainfouiComponent, {
        size: 'md',
        centered: true,
        backdrop: 'static'
      });
      modalRef.componentInstance.cameraId = cameraId;
      modalRef.componentInstance.sensorId = sensorId;
    }
  }

  playVideo(camera: CameraModel): void {
    const modalRef = this.modalService.open(FmvAttributeVisualizerModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.type = 'live';
    modalRef.componentInstance.sensorId = camera.id;
    modalRef.componentInstance.cameraSessionId = (camera as any).sessionId ?? undefined;
    modalRef.componentInstance.number = (camera as any).number ?? undefined;
    modalRef.componentInstance.mode = 'LIVE';
    modalRef.componentInstance.cameraUrl = (camera as any).url ?? '';
    modalRef.componentInstance.camera = camera;
  }

  get numberOfCameras(): number {
    return this.filteredCameras.length;
  }
}